import { FilterFn } from '@tanstack/react-table';
import { endOfDay } from 'date-fns';

import { FilterType } from '../../_openapi/portfolio-optimizer';
import { parseDate } from '../../utils/dateUtils';
import { DateFilterValue } from '../filters/DateFilter';
import { parseFilterStringToObject } from '../filters/filterUtils';
import { NumberFilterMode, NumberFilterValue } from '../filters/NumberFilter';

const listFilterFn: FilterFn<unknown> = (row, columnId, filterValue) => {
    if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
    const value = row.getValue(columnId);
    return value != null && filterValue.includes(String(value));
};

const makeNumberFilterFn =
    // Percentage columns store fractions but filter in displayed percent units.
    (scale = 1, roundTo?: number): FilterFn<unknown> =>
        (row, columnId, filterValue) => {
            const parsed = (typeof filterValue === 'string' ? parseFilterStringToObject(filterValue) : filterValue) as
                | NumberFilterValue
                | undefined;
            if (!parsed) return true;

            const raw = row.getValue(columnId);
            if (raw === null || raw === undefined || raw === '') return false;
            const scaled = Number(raw) * scale;
            if (Number.isNaN(scaled)) return false;
            const value = roundTo !== undefined ? Number(scaled.toFixed(roundTo)) : scaled;

            const toNum = (v?: string) => (v !== undefined && v !== '' ? Number(v) : undefined);
            const eq = toNum(parsed.eq);
            if (eq !== undefined) return value === eq;

            const checks: boolean[] = [];
            const gt = toNum(parsed.gt);
            const gte = toNum(parsed.gte);
            const lt = toNum(parsed.lt);
            const lte = toNum(parsed.lte);
            if (gt !== undefined) checks.push(value > gt);
            if (gte !== undefined) checks.push(value >= gte);
            if (lt !== undefined) checks.push(value < lt);
            if (lte !== undefined) checks.push(value <= lte);

            if (checks.length === 0) return true;
            return parsed.mode === NumberFilterMode.OR ? checks.some(Boolean) : checks.every(Boolean);
        };

const numberFilterFn = makeNumberFilterFn();

const dateFilterFn: FilterFn<unknown> = (row, columnId, filterValue) => {
    const parsed = (typeof filterValue === 'string' ? parseFilterStringToObject(filterValue) : filterValue) as
        | DateFilterValue
        | undefined;
    if (!parsed) return true;

    const raw = row.getValue(columnId);
    if (!raw) return false;
    const value = parseDate(raw as string)?.getTime();
    if (value === undefined || Number.isNaN(value)) return false;

    const from = parsed.from ? parseDate(parsed.from)?.getTime() : undefined;
    const to = parsed.to ? endOfDay(parseDate(parsed.to) ?? new Date(NaN)).getTime() : undefined; // inclusive day

    if (from !== undefined && value < from) return false;
    if (to !== undefined && value > to) return false;
    return true;
};

const booleanFilterFn: FilterFn<unknown> = (row, columnId, filterValue) => {
    if (filterValue === undefined || filterValue === null) return true;
    const expected = filterValue === true || filterValue === 'true';
    return Boolean(row.getValue(columnId)) === expected;
};

const textFilterFn: FilterFn<unknown> = (row, columnId, filterValue) => {
    const terms = Array.isArray(filterValue) ? filterValue : filterValue ? [String(filterValue)] : [];
    if (terms.length === 0) return true;
    const value = String(row.getValue(columnId) ?? '').toLowerCase();
    return terms.some((term) => value.includes(String(term).toLowerCase()));
};

export const getClientFilterFn = (
    filterType?: FilterType,
    options?: { isPercentage?: boolean; percentDecimals?: number },
): FilterFn<unknown> => {
    switch (filterType) {
        case FilterType.LIST:
            return listFilterFn;
        case FilterType.NUMBER:
            return options?.isPercentage ? makeNumberFilterFn(100, options.percentDecimals ?? 1) : numberFilterFn;
        case FilterType.DATE:
            return dateFilterFn;
        case FilterType.BOOLEAN:
            return booleanFilterFn;
        default:
            return textFilterFn;
    }
};
